import { attr, belongsTo } from "@ember-data/model";
import type Patient from "parog-web/models/patient";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type Performer from "parog-web/models/performer";
import JournalEvent from "parog-web/models/refunded-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type RiskStringEnum =
  | "MALADIE"
  | "MATERNITE"
  | "ACCIDENT"
  | "TRAVAIL"
  | "PREVENTION";

type ExemptionStringEnum = "ALD";

/**
 * Describe "LB" (actes de biologie) reimbursement.
 * Property descriptions are sometimes copy/pasted from the DMP specification.
 *
 * @see sel-mp-005_hr-integre-lps_v-1.9.pdf for details.
 *
 * The DMP feed provides a list of "execution", or reimbursement, each execution
 * has a list of "Acte Facturé" or "Prestation facturée".
 *
 * The biological-act-refunded represents one "Prestation Facturee" or "Acte Facturé", each one containing many measurements.
 */
export default class BiologicalActRefundedJournalEvent extends JournalEvent {
  static override type = "biological-act-refunded-journal-event";

  @attr() declare hospitalisation: any;

  /**
   * Nature du risque au titre duquel la prestation est prise en charge.
   * Valeurs possibles : «MALADIE», «MATERNITE», «ACCIDENT», «TRAVAIL»,
   * «PREVENTION»
   */
  @attr("string") declare risk: RiskStringEnum;

  /**
   * Valeur = «ALD» si présente
   */
  @attr("string") declare exemption: ExemptionStringEnum;

  @attr() declare measurements: any[];

  static override validations(intl: IntlService) {
    return {
      reimbursementsRetrievedJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.biological-act-refunded-journal-event.reimbursements-retrieved-journal-event"
        )
      }),
      measurements: validatePresence({
        presence: true,
        description: intl.t(
          "validations.biological-act-refunded-journal-event.measurements"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.biological-act-refunded-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      risk: validateInclusion({
        list: ["MALADIE", "MATERNITE", "ACCIDENT", "TRAVAIL", "PREVENTION"],
        allowBlank: true,
        description: intl.t(
          "validations.biological-act-refunded-journal-event.risk"
        )
      }),
      exemption: validateInclusion({
        list: ["ALD"],
        allowBlank: true,
        description: intl.t(
          "validations.biological-act-refunded-journal-event.exemption"
        )
      })
    };
  }

  //---- End of generated code ----

  @attr("performers") declare performers: Performer[];

  @belongsTo("patient", {
    async: false,
    inverse: "biologicalActRefundedJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "biological-act-refunded-journal-event": BiologicalActRefundedJournalEvent;
  }
}
